import { locationMatchingService } from '../location_matching/LocationMatchingService';

const GOOGLE_API_KEY_SEARCH = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SEARCH_ENGINE_ID = process.env.REACT_APP_GOOGLE_SEARCH_ID || '26c3d496c66a046e5'

// Store for learning data
let learningData = {
  positiveKeywords: new Set(),
  negativeKeywords: new Set(),
  positivePatterns: new Set(),
  negativePatterns: new Set(),
  feedbackHistory: [],
  contextPatterns: new Map(), // Store patterns for different contexts
  locationPreferences: new Map(), // Store location-specific preferences
  priceRanges: new Map(), // Store preferred price ranges
  sizePreferences: new Map(), // Store preferred sizes
  roomPreferences: new Map() // Store preferred room counts
};

// Keywords that indicate a result is likely an apartment listing
const APARTMENT_KEYWORDS = [
  // English keywords
  'apartment', 'flat', 'studio', 'penthouse', 'loft', 'duplex',
  'rent', 'lease', 'tenant', 'landlord', 'property', 'accommodation',
  'bedroom', 'bathroom', 'kitchen', 'living room', 'balcony', 'terrace',
  'furnished', 'unfurnished', 'available', 'for rent', 'long term',
  'monthly', 'deposit', 'utilities', 'included',
  // Polish keywords
  'mieszkanie', 'apartament', 'kawalerka', 'pokój', 'wynajem', 'najem',
  'sypialnia', 'łazienka', 'kuchnia', 'salon', 'balkon', 'taras',
  'umeblowane', 'nieumeblowane', 'dostępne', 'do wynajęcia', 'długoterminowy',
  'miesięczny', 'kaucja', 'czynsz', 'wliczone'
];

// Keywords that indicate a result is NOT an apartment listing
const NON_APARTMENT_KEYWORDS = [
  // English keywords
  'hotel', 'hostel', 'resort', 'villa', 'house', 'mansion',
  'commercial', 'office', 'shop', 'store', 'restaurant', 'cafe',
  'bar', 'club', 'gym', 'spa', 'pool', 'beach', 'tourist',
  'vacation', 'holiday', 'temporary', 'short stay', 'airbnb',
  'car', 'vehicle', 'camper', 'motorhome', 'rv', 'trailer',
  'garage', 'parking', 'storage', 'warehouse',
  // Polish keywords
  'hotel', 'hostel', 'resort', 'willa', 'dom', 'rezydencja',
  'lokal', 'biuro', 'sklep', 'restauracja', 'kawiarnia',
  'bar', 'klub', 'siłownia', 'spa', 'basen', 'plaża', 'turystyczny',
  'wakacje', 'święta', 'tymczasowy', 'krótkoterminowy', 'sprzedaż', 'sprzedam', 'sprzedaje',
  'samochód', 'auto', 'kamper', 'przyczepa', 'garaż', 'parking', 'magazyn',
  'wynajmę', 'wynajme', 'wynajem', 'najem', 'do wynajęcia', 'do wynajecia',
  // Marketplace categories
  'beds', 'frames', 'furniture', 'electronics', 'clothing', 'shoes',
  'accessories', 'toys', 'games', 'sports', 'hobbies', 'books',
  'music', 'movies', 'collectibles', 'art', 'crafts', 'jewelry',
  'watches', 'beauty', 'health', 'baby', 'pet', 'garden',
  'tools', 'home', 'decor', 'kitchen', 'dining', 'living',
  'bedroom', 'bathroom', 'outdoor', 'automotive', 'motorcycle',
  'boats', 'campers', 'rvs', 'trailers', 'parts', 'accessories',
  // Polish marketplace categories
  'meble', 'elektronika', 'ubrania', 'buty', 'akcesoria', 'zabawki',
  'gry', 'sport', 'hobby', 'książki', 'muzyka', 'filmy',
  'kolekcje', 'sztuka', 'biżuteria', 'zegarki', 'kosmetyki',
  'zdrowie', 'dziecko', 'zwierzęta', 'ogród', 'narzędzia',
  'dom', 'dekoracje', 'kuchnia', 'jadalnia', 'salon',
  'sypialnia', 'łazienka', 'na zewnątrz', 'motoryzacja',
  'motocykle', 'łodzie', 'kampery', 'przyczepy', 'części',
  'akcesoria'
];

// Function to extract keywords from text
const extractKeywords = (text) => {
  const words = text.toLowerCase().split(/\W+/);
  return words.filter(word => word.length > 3); // Filter out short words
};

// Function to extract patterns from text
const extractPatterns = (text) => {
  const patterns = [];
  
  // Extract price patterns (PLN and EUR)
  const pricePatternPLN = text.match(/(\d+[.,]?\d*)\s*(?:PLN|zł|złoty|złote)/i);
  const pricePatternEUR = text.match(/(\d+[.,]?\d*)\s*(?:EUR|€|euro)/i);
  if (pricePatternPLN) patterns.push({ type: 'price', value: parseFloat(pricePatternPLN[1]), currency: 'PLN' });
  if (pricePatternEUR) patterns.push({ type: 'price', value: parseFloat(pricePatternEUR[1]), currency: 'EUR' });
  
  // Extract size patterns (m² and m2)
  const sizePattern = text.match(/(\d+[.,]?\d*)\s*(?:m²|m2|metr)/i);
  if (sizePattern) patterns.push({ type: 'size', value: parseFloat(sizePattern[1]) });
  
  // Extract room patterns (Polish and English)
  const roomPattern = text.match(/(\d+)\s*(?:pokój|pokoi|room|bedroom|bed|hab)/i);
  if (roomPattern) patterns.push({ type: 'rooms', value: parseInt(roomPattern[1]) });
  
  // Extract location patterns - street names and housing estates
  const locationPatterns = [
    // Street patterns
    /(?:ul\.|ulica|al\.|aleja|pl\.|plac)\s+([^,]+?)(?:\s+\d+)?/i,
    // Housing estate patterns
    /(?:osiedle|os\.)\s+([^,]+)/i,
    // District + street patterns
    /(?:w|w\s+okolicach|w\s+pobliżu)\s+([^,]+?)(?:\s+(?:ul\.|ulica|al\.|aleja|pl\.|plac)\s+[^,]+)?/i,
    // General location patterns
    /(?:w|w\s+okolicach|w\s+pobliżu|in|at|en)\s+([^,]+)/i
  ];

  for (const pattern of locationPatterns) {
    const match = text.match(pattern);
    if (match) {
      const location = match[1].trim();
      // Clean up the location string
      const cleanLocation = location
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/^[,\s]+|[,\s]+$/g, '') // Remove leading/trailing commas and spaces
        .replace(/\s*,\s*/g, ', '); // Standardize comma spacing
      
      patterns.push({ type: 'location', value: cleanLocation });
      break; // Stop after finding the first matching pattern
    }
  }
  
  return patterns;
};

// Function to save learning data to backend
const saveLearningData = async (feedback) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/learning/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        result: feedback.result,
        isPositive: feedback.isPositive,
        keywords: feedback.keywords,
        patterns: feedback.patterns,
        context: feedback.context,
        timestamp: feedback.timestamp
      })
    });

    if (!response.ok) {
      console.error('Failed toчном save feedback to server');
    }
  } catch (error) {
    console.error('Error saving feedback:', error);
  }
};

// Function to load learning data from backend
const loadLearningData = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/learning/data`);
    if (response.ok) {
      const data = await response.json();
      
      // Update local learning data with server data
      learningData.positiveKeywords = new Set(data.positiveKeywords || []);
      learningData.negativeKeywords = new Set(data.negativeKeywords || []);
      learningData.positivePatterns = new Set(data.positivePatterns || []);
      learningData.negativePatterns = new Set(data.negativePatterns || []);
      learningData.priceRanges = new Map(Object.entries(data.priceRanges || {}));
      learningData.sizePreferences = new Map(Object.entries(data.sizePreferences || {}));
      learningData.roomPreferences = new Map(Object.entries(data.roomPreferences || {}));
      learningData.locationPreferences = new Map(Object.entries(data.locationPreferencesknockout));
    }
  } catch (error) {
    console.error('Error loading learning data:', error);
  }
};

// Function to learn from feedback with enhanced pattern recognition
const learnFromFeedback = async (result, isPositive) => {
  // Extract keywords and patterns
  const titleKeywords = extractKeywords(result.title);
  const snippetKeywords = extractKeywords(result.snippet);
  const patterns = extractPatterns(result.snippet);
  
  // Update local learning data
  if (isPositive) {
    titleKeywords.forEach(keyword => learningData.positiveKeywords.add(keyword));
    snippetKeywords.forEach(keyword => learningData.positiveKeywords.add(keyword));
    patterns.forEach(pattern => learningData.positivePatterns.add(JSON.stringify(pattern)));
  } else {
    titleKeywords.forEach(keyword => learningData.negativeKeywords.add(keyword));
    snippetKeywords.forEach(keyword => learningData.negativeKeywords.add(keyword));
    patterns.forEach(pattern => learningData.negativePatterns.add(JSON.stringify(pattern)));
  }

  // Update context-specific preferences
  if (isPositive) {
    patterns.forEach(pattern => {
      switch (pattern.type) {
        case 'price':
          updatePreferenceMap(learningData.priceRanges, pattern.value);
          break;
        case 'size':
          updatePreferenceMap(learningData.sizePreferences, pattern.value);
          break;
        case 'rooms':
          updatePreferenceMap(learningData.roomPreferences, pattern.value);
          break;
        case 'location':
          updatePreferenceMap(learningData.locationPreferences, pattern.value);
          break;
      }
    });
  }

  // Prepare feedback data
  const feedback = {
    result,
    isPositive,
    timestamp: new Date(),
    keywords: [...new Set([...titleKeywords, ...snippetKeywords])],
    patterns,
    context: {
      price: result.price,
      size: result.squareMeters,
      rooms: result.rooms,
      location: result.location
    }
  };

  // Store feedback in history
  learningData.feedbackHistory.push(feedback);

  // Save to backend
  await saveLearningData(feedback);
};

// Helper function to update preference maps
const updatePreferenceMap = (map, value) => {
  const range = Math.floor(value / 10) * 10; // Group values into ranges
  map.set(range, (map.get(range) || 0) + 1);
};

// Enhanced relevance scoring with learned patterns
const calculateRelevanceScore = (result) => {
  let score = 0;
  const text = `${result.title} ${result.snippet}`.toLowerCase();
  const keywords = extractKeywords(text);
  const patterns = extractPatterns(text);
  
  // Score based on learned keywords
  keywords.forEach(keyword => {
    if (learningData.positiveKeywords.has(keyword)) {
      score += 2;
    }
    if (learningData.negativeKeywords.has(keyword)) {
      score -= 2;
    }
  });

  // Score based on learned patterns
  patterns.forEach(pattern => {
    const patternStr = JSON.stringify(pattern);
    if (learningData.positivePatterns.has(patternStr)) {
      score += 3;
    }
    if (learningData.negativePatterns.has(patternStr)) {
      score -= 3;
    }
  });

  // Score based on context preferences
  if (result.price) {
    const priceRange = Math.floor(result.price / 10) * 10;
    score += (learningData.priceRanges.get(priceRange) || 0) * 0.5;
  }
  
  if (result.squareMeters) {
    const sizeRange = Math.floor(result.squareMeters / 10) * 10;
    score += (learningData.sizePreferences.get(sizeRange) || 0) * 0.5;
  }
  
  if (result.rooms) {
    score += (learningData.roomPreferences.get(result.rooms) || 0) * 0.5;
  }
  
  if (result.location) {
    score += (learningData.locationPreferences.get(result.location) || 0) * 0.5;
  }

  // Original scoring logic
  APARTMENT_KEYWORDS.forEach(keyword => {
    if (text.includes(keyword)) {
      score += 1;
    }
  });
  
  NON_APARTMENT_KEYWORDS.forEach(keyword => {
    if (text.includes(keyword)) {
      score -= 2;
    }
  });
  
  // Bonus points for having key apartment details
  if (result.price) score += 2;
  if (result.squareMeters) score += 2;
  if (result.rooms) score += 2;
  if (result.image) score += 1;
  
  // Bonus for having a clear location
  if (result.location && result.location.length > 3) score += 1;
  
  // Penalize very short titles/snippets
  if (result.title.length < 10) score -= 2;
  if (result.snippet.length < 50) score -= 2;
  
  // Penalize listings with missing or old dates
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  if (!result.lastUpdatedDate || result.lastUpdatedDate < thirtyDaysAgo) {
    score -= 2;
  }

  return score;
};

// Function to determine if a result is an individual apartment listing
const isNonApartmentListing = (result) => {
  // Score the result based on various factors
  let score = 0;
  
  // First check if it's a sale listing - if so, return false immediately
  const title = result.title.toLowerCase();
  const snippet = result.snippet.toLowerCase();
  const url = result.link.toLowerCase();
  
  // Check for marketplace categories in URL
  const marketplaceCategories = [
    '/beds', '/frames', '/furniture', '/electronics', '/clothing', '/shoes',
    '/accessories', '/toys', '/games', '/sports', '/hobbies', '/books',
    '/music', '/movies', '/collectibles', '/art', '/crafts', '/jewelry',
    '/watches', '/beauty', '/health', '/baby', '/pet', '/garden',
    '/tools', '/home', '/decor', '/kitchen', '/dining', '/living',
    '/bedroom', '/bathroom', '/outdoor', '/automotive', '/motorcycle',
    '/boats', '/campers', '/rvs', '/trailers', '/parts', '/accessories',
    // Polish categories
    '/meble', '/elektronika', '/ubrania', '/buty', '/akcesoria', '/zabawki',
    '/gry', '/sport', '/hobby', '/ksiazki', '/muzyka', '/filmy',
    '/kolekcje', '/sztuka', '/bizuteria', '/zegarki', '/kosmetyki',
    '/zdrowie', '/dziecko', '/zwierzeta', '/ogrod', '/narzedzia',
    '/dom', '/dekoracje', '/kuchnia', '/jadalnia', '/salon',
    '/sypialnia', '/lazienka', '/na-zewnatrz', '/motoryzacja',
    '/motocykle', '/lodzie', '/kampery', '/przyczepy', '/czesci',
    '/akcesoria'
  ];

  // If URL contains any marketplace category, it's not an apartment listing
  if (marketplaceCategories.some(category => url.includes(category))) {
    return true;
  }

  const saleKeywords = [
    'na sprzedaż', 'sprzedam', 'sprzedaż', 'do sprzedaży', 'na sprzedaz', 'sprzedaz',
    'for sale', 'buy', 'purchase', 'sold', 'sprzedane', 'sprzedana', 'sprzedany'
  ];
  
  if (saleKeywords.some(keyword => title.includes(keyword) || snippet.includes(keyword))) {
    return true;
  }
  
  // Extract room number from title if not already present
  if (!result.rooms) {
    // Match patterns like "1 pokój", "2 pokoje", "3-pokojowe", etc.
    const roomMatch = title.match(/(\d+)\s*(?:-|\s)?(?:pok[óo]j|pokoi|pokoj[oó]w|pokojowe)/i);
    if (roomMatch) {
      result.rooms = parseInt(roomMatch[1], 10);
    }
  }
  
  // Check URL patterns that indicate individual listings
  const urlPatterns = [
    // otodom.pl specific patterns - must have ID and be an individual listing
    /otodom\.pl\/pl\/oferta\/.*[a-zA-Z0-9]{8,}/i,
    // olx.pl specific patterns - must have ID
    /olx\.pl\/d\/oferta\/.*[a-zA-Z0-9]{8,}/i,
    // facebook.com/marketplace specific patterns - must have ID
    /facebook\.com\/marketplace\/item\/[a-zA-Z0-9]{8,}/i
  ];

  // Check for category/listing pages
  const categoryPatterns = [
    /otodom\.pl\/pl\/wyniki/i,
    /otodom\.pl\/pl\/lista/i,
    /otodom\.pl\/pl\/przegladaj/i,
    /otodom\.pl\/pl\/szukaj/i,
    /otodom\.pl\/pl\/wynajem/i,
    /otodom\.pl\/pl\/sprzedaz/i,
    /olx\.pl\/d\/wyniki/i,
    /olx\.pl\/d\/lista/i,
    /olx\.pl\/d\/przegladaj/i,
    /olx\.pl\/d\/szukaj/i,
    /facebook\.com\/marketplace\/category/i
  ];

  // If URL matches any category pattern, it's not an individual listing
  if (categoryPatterns.some(pattern => pattern.test(url))) {
    return true;
  }

  // If URL doesn't match any of the individual listing patterns (no ID), it's not an individual apartment listing
  if (!urlPatterns.some(pattern => pattern.test(url))) {
    return true;
  }

  // Check if the source is a known apartment listing site
  const source = result.source.toLowerCase();
  if (source.includes('otodom.pl')) {
    score += 2; // Higher score for otodom.pl
  } else if (source.includes('olx.pl')) {
    score += 2; // Higher score for olx.pl
  } else if (source.includes('facebook.com/marketplace')) {
    score += 1;
  }

  // Check for Polish-specific keywords
  const polishKeywords = [
    'mieszkanie', 'apartament', 'kawalerka', 'wynajem', 'najem',
    'sypialnia', 'łazienka', 'kuchnia', 'salon', 'balkon', 'taras',
    'umeblowane', 'nieumeblowane', 'dostępne', 'do wynajęcia', 'wynajme',
    'dlugoterminowy','czynsz', 'kaucja', 'opłaty', 'media', 'prąd', 'woda', 'gaz',
    'internet', 'telewizja', 'parking', 'garaż', 'piwnica', 'strych'
  ];
  
  polishKeywords.forEach(keyword => {
    if (title.includes(keyword) || snippet.includes(keyword)) {
      score += 1;
    }
  });

  // Return true if the score is low, indicating a non-apartment listing
  return score < 3;
};

// Function to check if a listing is unavailable
const isUnavailableListing = (result) => {
  const url = result.link.toLowerCase();
  const title = result.title.toLowerCase();
  const snippet = result.snippet.toLowerCase();

  // Check for unavailable indicators in URL
  if (url.includes('#from404') || 
      url.includes('nieaktualne') || 
      url.includes('unavailable') ||
      url.includes('expired')) {
    return true;
  }

  // Check for unavailable indicators in title and snippet
  const unavailableKeywords = [
    'nieaktualne', 'nieaktualna', 'nieaktualny',
    'unavailable', 'expired', 'sold', 'rented',
    'zarezerwowane', 'zarezerwowana', 'zarezerwowany',
    'sprzedane', 'sprzedana', 'sprzedany',
    'wynajęte', 'wynajęta', 'wynajęty'
  ];

  return unavailableKeywords.some(keyword => 
    title.includes(keyword) || snippet.includes(keyword)
  );
};

// Export feedback function
export const provideFeedback = (result, isPositive) => {
  learnFromFeedback(result, isPositive);
};

export const searchApartments = async (filters) => {
  try {
    // Validate and set default values for filters
    const validatedFilters = {
      place_description: '',
      minSquareMeters: filters?.min_square_footage || 0,
      rooms: filters?.rooms || 0,
      location: filters?.location || null // Add location from Google Places API
    };

    // Handle place description
    if (filters?.place_description && typeof filters.place_description === 'string') {
      validatedFilters.place_description = filters.place_description;
    } else {
      throw new Error('A valid place description is required for apartment search');
    }

    // Construct the search query based on available filters
    let searchQuery = `mieszkanie do wynajęcia ${validatedFilters.place_description}`;
    
    // Add square meters to query if specified
    if (validatedFilters.minSquareMeters > 0) {
      searchQuery += ` ${validatedFilters.minSquareMeters}m²`;
    }

    // Construct site-specific search queries
    const otodomQuery = `site:otodom.pl/pl/oferta/ ${searchQuery} -inurl:wyniki -inurl:lista -inurl:przegladaj -inurl:szukaj`;
    const olxQuery = `site:olx.pl/d/oferta/ ${searchQuery}`;
    const facebookQuery = `site:facebook.com/marketplace/ ${searchQuery}`;

    // Validate API configuration
    if (!GOOGLE_API_KEY_SEARCH) {
      throw new Error('Google API Key is not configured');
    }
    if (!SEARCH_ENGINE_ID) {
      throw new Error('Search Engine ID is not configured');
    }

    // Fetch results from each source
    const [otodomResponse, olxResponse, facebookResponse] = await Promise.all([
      fetch(`https://www.googleapis.com/customsearch/v1?key=${GOOGLE_API_KEY_SEARCH}&cx=${SEARCH_ENGINE_ID}&q=${encodeURIComponent(otodomQuery)}&num=10&dateRestrict=m1`),
      fetch(`https://www.googleapis.com/customsearch/v1?key=${GOOGLE_API_KEY_SEARCH}&cx=${SEARCH_ENGINE_ID}&q=${encodeURIComponent(olxQuery)}&num=10&dateRestrict=m1`),
      fetch(`https://www.googleapis.com/customsearch/v1?key=${GOOGLE_API_KEY_SEARCH}&cx=${SEARCH_ENGINE_ID}&q=${encodeURIComponent(facebookQuery)}&num=10&dateRestrict=m1`)
    ]);

    // Process responses
    const processResponse = async (response, source) => {
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Google Search API request failed for ${source}: ${errorData.error?.message || response.statusText}`);
      }
      const data = await response.json();
      return data.items || [];
    };

    try {
      const [otodomItems, olxItems, facebookItems] = await Promise.all([
        processResponse(otodomResponse, 'Otodom'),
        processResponse(olxResponse, 'OLX'),
        processResponse(facebookResponse, 'Facebook')
      ]);

      // Combine all items
      const allItems = [...otodomItems, ...olxItems, ...facebookItems];
      
      if (allItems.length === 0) {
        return [];
      }
      
      // Calculate date 30 days ago
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const currentYear = now.getFullYear();
      
      // Process and format the search results
      const formattedResults = allItems.map(item => {
        // Log the snippet for debugging
        // console.log('Processing snippet:', {
        //   title: item.title,
        //   snippet: item.snippet,
        //   link: item.link
        // });

        // Extract location using the location matching service
        const extractedLocation = locationMatchingService.extractLocation(item.snippet) || 
                                locationMatchingService.extractLocation(item.title);

        // Log extracted location
        // console.log('Extracted location:', {
        //   title: item.title,
        //   extractedLocation,
        //   googlePlace: validatedFilters.location
        // });

        // Extract square meters and rooms from the snippet if available
        const squareMetersMatch = item.snippet.match(/(\d+[.,]?\d*)\s*(?:m²|m2|metr)/i);
        const roomsMatch = item.snippet.match(/(\d+)\s*(?:pokój|pokoi|room|bedroom|bed|hab)/i);
        const priceMatchPLN = item.snippet.match(/(\d+[.,]?\d*)\s*(?:PLN|zł|złoty|złote)/i);
        const priceMatchEUR = item.snippet.match(/(\d+[.,]?\d*)\s*(?:EUR|€|euro)/i);
        
        // Clean up the title by removing website names and common prefixes
        const cleanTitle = item.title
          .replace(/(?:olx|otodom|facebook)\.(?:pl|com)/i, '')
          .replace(/^[^:]*:\s*/, '')
          .replace(/(?:wyniki wyszukiwania|lista|wszystkie mieszkania|zobacz wszystkie|przeglądaj|szukaj)/i, '')
          .trim();

        // If no location found from the location matching service, try to extract it from the snippet
        let location = extractedLocation;
        if (!location) {
          // Look for city names first
          const cityMatch = item.snippet.match(/(?:^|\s)(Wrocław|Warszawa|Kraków|Poznań|Gdańsk|Łódź|Wrocław|Warszawa|Krakow|Poznan|Gdansk|Lodz)(?:\s*,\s*[^,]+)?/i);
          if (cityMatch) {
            location = cityMatch[1].trim();
          } else {
            // Then try to find a street name followed by city
            const streetCityMatch = item.snippet.match(/([A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+)*)(?:\s*,\s*(?:Wrocław|Warszawa|Kraków|Poznań|Gdańsk|Łódź|Wrocław|Warszawa|Krakow|Poznan|Gdansk|Lodz))/i);
            if (streetCityMatch) {
              location = streetCityMatch[0].trim();
            }
          }
        }

        // Skip if the location is a date-related word
        const dateWords = ['may', 'days', 'ago', 'hours', 'hour', 'minutes', 'minute', 'seconds', 'second'];
        if (location && dateWords.some(word => location.toLowerCase().includes(word))) {
          location = null;
        }

        // If still no location found, use the filter's place description
        if (!location) {
          location = validatedFilters.place_description;
        } else {
          // Clean up the location string
          location = location
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/^[,\s]+|[,\s]+$/g, '') // Remove leading/trailing commas and spaces
            .replace(/\s*,\s*/g, ', '); // Standardize comma spacing
          
          // Check if the extracted location contains the filter's place description
          const filterLocation = validatedFilters.place_description.toLowerCase();
          if (!location.toLowerCase().includes(filterLocation)) {
            // If the extracted location doesn't match the filter, use the filter's location
            location = validatedFilters.place_description;
          }
        }

        // Get image URL from pagemap
        const imageUrl = item.pagemap?.cse_image?.[0]?.src || 
                        item.pagemap?.cse_thumbnail?.[0]?.src || 
                        item.pagemap?.metatags?.[0]?.['og:image'] ||
                        item.pagemap?.metatags?.[0]?.['twitter:image'] ||
                        null;

        // Parse the last updated date - try multiple sources
        let lastUpdatedDate = null;
        const lastUpdated = item.pagemap?.metatags?.[0]?.['article:modified_time'] || 
                           item.pagemap?.metatags?.[0]?.['og:updated_time'] ||
                           item.pagemap?.metatags?.[0]?.['last-modified'] ||
                           item.pagemap?.metatags?.[0]?.['date'] ||
                           item.pagemap?.metatags?.[0]?.['pubdate'] ||
                           null;

        if (lastUpdated) {
          lastUpdatedDate = new Date(lastUpdated);
          // Discard invalid or future dates
          if (isNaN(lastUpdatedDate) || lastUpdatedDate > now) {
            lastUpdatedDate = null;
          }
        }

        // Try to find date patterns in snippet if no valid metadata date
        if (!lastUpdatedDate) {
          const datePatterns = [
            // Relative time patterns (prioritized for recency)
            { pattern: /(\d+)\s*(?:dni|dni\s+temu|days?\s+ago)/i, handler: (match) => {
              const days = parseInt(match[1]);
              const date = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
              date.setFullYear(currentYear);
              return date;
            }},
            { pattern: /(\d+)\s*(?:godzin|godzin\s+temu|hours?\s+ago)/i, handler: (match) => {
              const hours = parseInt(match[1]);
              const date = new Date(now.getTime() - hours * 60 * 60 * 1000);
              date.setFullYear(currentYear);
              return date;
            }},
            { pattern: /(\d+)\s*(?:minut|minut\s+temu|minutes?\s+ago)/i, handler: (match) => {
              const minutes = parseInt(match[1]);
              const date = new Date(now.getTime() - minutes * 60 * 1000);
              date.setFullYear(currentYear);
              return date;
            }},
            { pattern: /(\d+)\s*(?:tygodni|tygodni\s+temu|weeks?\s+ago)/i, handler: (match) => {
              const weeks = parseInt(match[1]);
              const date = new Date(now.getTime() - weeks * 7 * 24 * 60 * 60 * 1000);
              date.setFullYear(currentYear);
              return date;
            }},
            { pattern: /(\d+)\s*(?:miesięcy|miesięcy\s+temu|months?\s+ago)/i, handler: (match) => {
              const months = parseInt(match[1]);
              const date = new Date(now);
              date.setMonth(now.getMonth() - months);
              date.setFullYear(currentYear);
              return date;
            }},
            // English month name pattern (e.g., "June 12, 2025")
            { pattern: /(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/i, handler: (match) => {
              const monthMap = {
                'jan': 0, 'january': 0, 'feb': 1, 'february': 1, 'mar': 2, 'march': 2,
                'apr': 3, 'april': 3, 'may': 4, 'jun': 5, 'june': 5, 'jul': 6, 'july': 6,
                'aug': 7, 'august': 7, 'sep': 8, 'september': 8, 'oct': 9, 'october': 9,
                'nov': 10, 'november': 10, 'dec': 11, 'december': 11
              };
              const monthName = match[0].split(' ')[0].toLowerCase();
              const month = monthMap[monthName];
              const day = parseInt(match[1]);
              const year = parseInt(match[2]);
              const date = new Date(year, month, day);
              // If date is in the future, assume it's from the current year or previous year
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            // Absolute date patterns
            { pattern: /(\d{1,2})\.(\d{1,2})\.(\d{4})/i, handler: (match) => {
              const day = parseInt(match[1]);
              const month = parseInt(match[2]) - 1;
              const year = parseInt(match[3]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            { pattern: /(\d{1,2})\/(\d{1,2})\/(\d{4})/i, handler: (match) => {
              const day = parseInt(match[1]);
              const month = parseInt(match[2]) - 1;
              const year = parseInt(match[3]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            { pattern: /(\d{4})-(\d{1,2})-(\d{1,2})/i, handler: (match) => {
              const year = parseInt(match[1]);
              const month = parseInt(match[2]) - 1;
              const day = parseInt(match[3]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            { pattern: /dodano\s+(\d{1,2})\.(\d{1,2})\.(\d{4})/i, handler: (match) => {
              const day = parseInt(match[1]);
              const month = parseInt(match[2]) - 1;
              const year = parseInt(match[3]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            { pattern: /dodano\s+(\d{1,2})\/(\d{1,2})\/(\d{4})/i, handler: (match) => {
              const day = parseInt(match[1]);
              const month = parseInt(match[2]) - 1;
              const year = parseInt(match[3]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            { pattern: /dodano\s+(\d{4})-(\d{1,2})-(\d{1,2})/i, handler: (match) => {
              const year = parseInt(match[1]);
              const month = parseInt(match[2]) - 1;
              const day = parseInt(match[3]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }},
            // Polish month name pattern (e.g., "dodano w 12 maja 2025")
            { pattern: /dodano\s+w\s+(\d{1,2})\s+(?:stycznia|lutego|marca|kwietnia|maja|czerwca|lipca|sierpnia|września|października|listopada|grudnia)\s+(\d{4})/i, handler: (match) => {
              const polishMonthMap = {
                'stycznia': 0, 'lutego': 1, 'marca': 2, 'kwietnia': 3, 'maja': 4,
                'czerwca': 5, 'lipca': 6, 'sierpnia': 7, 'września': 8,
                'października': 9, 'listopada': 10, 'grudnia': 11
              };
              const day = parseInt(match[1]);
              const month = polishMonthMap[match[0].split(' ')[2].toLowerCase()];
              const year = parseInt(match[2]);
              const date = new Date(year, month, day);
              if (date > now) {
                date.setFullYear(currentYear - (date.getFullYear() > currentYear ? 1 : 0));
              }
              return date;
            }}
          ];

          for (const { pattern, handler } of datePatterns) {
            const match = item.snippet.match(pattern);
            if (match) {
              lastUpdatedDate = handler(match);
              break;
            }
          }
        }

        // If no valid date found, use a fallback date (30 days ago)
        if (!lastUpdatedDate || isNaN(lastUpdatedDate)) {
          lastUpdatedDate = new Date(thirtyDaysAgo);
        }

        // Ensure date is within the last 30 days
        if (lastUpdatedDate < thirtyDaysAgo) {
          lastUpdatedDate = new Date(thirtyDaysAgo);
        }


        // Extract additional details from the URL
        const url = item.link.toLowerCase();
        let additionalDetails = {};
        
        // Extract size from URL if available (e.g., 96m2)
        const urlSizeMatch = url.match(/(\d+)m2/);
        if (urlSizeMatch && !squareMetersMatch) {
          additionalDetails.squareMeters = parseInt(urlSizeMatch[1]);
        }
        
        // Extract room count from URL if available
        const urlRoomsMatch = url.match(/(\d+)-pokoj/);
        if (urlRoomsMatch && !roomsMatch) {
          additionalDetails.rooms = parseInt(urlRoomsMatch[1]);
        }

        const result = {
          title: cleanTitle,
          location: extractedLocation,
          googlePlace: validatedFilters.location,
          price: priceMatchPLN ? parseFloat(priceMatchPLN[1].replace(',', '.')) : 
                 priceMatchEUR ? parseFloat(priceMatchEUR[1].replace(',', '.')) : null,
          priceCurrency: priceMatchPLN ? 'PLN' : priceMatchEUR ? 'EUR' : null,
          squareMeters: squareMetersMatch ? parseFloat(squareMetersMatch[1].replace(',', '.')) : 
                       additionalDetails.squareMeters || null,
          rooms: roomsMatch ? parseInt(roomsMatch[1]) : 
                 additionalDetails.rooms || null,
          link: item.link,
          snippet: item.snippet,
          source: new URL(item.link).hostname,
          lastUpdated: lastUpdated,
          lastUpdatedDate: lastUpdatedDate,
          image: imageUrl,
          relevanceScore: calculateRelevanceScore({
            title: cleanTitle,
            snippet: item.snippet,
            price: priceMatchPLN ? parseFloat(priceMatchPLN[1].replace(',', '.')) : 
                   priceMatchEUR ? parseFloat(priceMatchEUR[1].replace(',', '.')) : null,
            squareMeters: squareMetersMatch ? parseFloat(squareMetersMatch[1].replace(',', '.')) : 
                         additionalDetails.squareMeters || null,
            rooms: roomsMatch ? parseInt(roomsMatch[1]) : 
                   additionalDetails.rooms || null,
            location: extractedLocation,
            image: imageUrl
          })
        };

        return result;
      }).filter(result => {
        // Filter out results that don't meet the specified criteria
        let isValid = true;
        
        // Check if it's a non-apartment listing
        if (isNonApartmentListing(result)) {
          return false;
        }

        // Check if the listing is unavailable
        if (isUnavailableListing(result)) {
          return false;
        }
        
        // Check square meters if specified
        if (validatedFilters.minSquareMeters > 0) {
          isValid = isValid && result.squareMeters && result.squareMeters >= validatedFilters.minSquareMeters;
        }
        
        // Check rooms if specified
        if (validatedFilters.rooms > 0) {
          isValid = isValid && result.rooms && result.rooms >= validatedFilters.rooms;
        }

        // Check location if specified
        if (validatedFilters.location) {
          isValid = isValid && locationMatchingService.matchLocation(
            result.location,
            validatedFilters.location,
            validatedFilters.location.radius || 10000
          );
        }

        // Filter out results with negative relevance scores
        isValid = isValid && result.relevanceScore > 0;

        return isValid;
      });

      // Sort results by date (newest first) and then by relevance score
      formattedResults.sort((a, b) => {
        // Ensure both dates are valid
        const dateA = a.lastUpdatedDate || new Date(thirtyDaysAgo);
        const dateB = b.lastUpdatedDate || new Date(thirtyDaysAgo);

        // Sort by date (newest first)
        const dateDiff = dateB - dateA;
        if (dateDiff !== 0) return dateDiff;

        // If dates are equal, sort by relevance score
        return b.relevanceScore - a.relevanceScore;
      });

      return formattedResults;
    } catch (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

// Load learning data on initialization
loadLearningData();